/**
 * How a failed passkey or step-up call is described: the shape every page
 * acts on, and the rules for reading the server's error. Pure, with no
 * imports that touch the browser or the auth store, so the signing and
 * enrolment logic that branches on it is unit tested on its own.
 */
import { memberErrorMessage } from "./memberErrors";

/** A failed call, normalised to what a page acts on. */
export type StepUpFailure = {
  /** HTTP status, or null when the request never reached the server. */
  status: number | null;
  /** Machine-readable code, when the server sent one (`reauth_required`). */
  code: string | null;
  message: string;
};

export const REAUTH_REQUIRED = "reauth_required";

export const NETWORK_MESSAGE =
  "We could not reach Ploy. Check your connection, then try again.";
export const SESSION_MESSAGE =
  "Your session has expired. Reload this page, sign in, and try again.";
export const REAUTH_MESSAGE = "Confirm it's you by signing in again.";

const asRecord = (body: unknown): Record<string, unknown> | null =>
  body && typeof body === "object" ? (body as Record<string, unknown>) : null;

/**
 * One failure per response. A session or re-auth refusal is described by its
 * status and code; anything else prefers the server's own words, which are
 * written for a person, and falls back to the caller's.
 */
export const toFailure = (
  status: number | null,
  body: unknown,
  fallback: string,
): StepUpFailure => {
  const record = asRecord(body);
  const code =
    typeof record?.code === "string"
      ? record.code
      : record?.error === REAUTH_REQUIRED
        ? REAUTH_REQUIRED
        : null;

  if (code === REAUTH_REQUIRED) return { status, code, message: REAUTH_MESSAGE };
  if (status === 401) return { status, code, message: SESSION_MESSAGE };
  // A 404 body says "Not Found", which tells a person nothing. The caller's
  // fallback is written for the thing that was missing.
  if (status === 404) return { status, code, message: fallback };
  return { status, code, message: memberErrorMessage(body, fallback) };
};

export const isReauthRequired = (failure: unknown): boolean =>
  asRecord(failure)?.code === REAUTH_REQUIRED;

export const isStepUpFailure = (value: unknown): value is StepUpFailure => {
  const record = asRecord(value);
  return Boolean(record && typeof record.message === "string" && "status" in record);
};

/** The message to show for anything a call threw. */
export const failureMessage = (error: unknown, fallback: string): string =>
  isStepUpFailure(error) ? error.message : fallback;

/**
 * How the server refuses an unsigned approval on a stage that needs a passkey
 * (HandleApprovalResponse's `@signature_required`), as a 400 whose `error` is
 * this sentence and then a pointer to the portal. It carries no code, so the
 * words are matched; only its first sentence, which names the cause.
 */
export const PASSKEY_APPROVAL_REFUSAL = "This approval step needs your passkey.";

/** Whether a rejected task answer is that refusal, as `membersApi` rejects with it. */
export const isPasskeyApprovalRefusal = (error: unknown): boolean => {
  const message = asRecord(error)?.error;
  return typeof message === "string" && message.startsWith(PASSKEY_APPROVAL_REFUSAL);
};

// The words for each call's failure, defined once and shared by the calls
// (as fallbacks for a server message) and the pages that describe them.
export const CHALLENGE_NOT_FOUND_MESSAGE =
  "This signing link is not valid for you. Open it again from where you started.";
export const START_SIGNING_FAILED_MESSAGE = "We could not start signing. Try again.";
export const VERIFY_FAILED_MESSAGE = "Your passkey could not be checked. Try again.";
export const LOAD_PASSKEYS_FAILED_MESSAGE =
  "We could not load your passkeys. Reload the page to try again.";
export const START_REGISTRATION_FAILED_MESSAGE =
  "We could not start adding a passkey. Try again.";
export const SAVE_PASSKEY_FAILED_MESSAGE = "Your passkey could not be saved. Try again.";
export const RENAME_PASSKEY_FAILED_MESSAGE =
  "That passkey could not be renamed. Try again.";
export const REMOVE_PASSKEY_FAILED_MESSAGE =
  "That passkey could not be removed. It may already be gone.";
